import { z } from 'zod';
import { UNITS } from '../constants';

/**
 * NEW — the AI invoice reader (apps/api `POST /api/invoices/read`): a supplier invoice photographed at the
 * shop goes in, what is written on it comes out. Declarations only; the model call lives in apps/api.
 */

/** What the browser sends: it scales the photo down first, so a few hundred kilobytes is the norm. */
export const INVOICE_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp'] as const;
export type InvoiceImageType = (typeof INVOICE_IMAGE_TYPES)[number];
/** NEW — a PDF invoice goes as it is: the model reads its text and sees its pages. */
export const INVOICE_FILE_TYPES = [...INVOICE_IMAGE_TYPES, 'application/pdf'] as const;
/** Characters of base64; the API accepts a body of about this size on the read route only. */
export const INVOICE_IMAGE_MAX_BASE64 = 6 * 1024 * 1024;

export const invoiceReadRequestSchema = z.object({
  /** The photo or the PDF, base64. */
  image: z
    .string()
    .min(1)
    .max(INVOICE_IMAGE_MAX_BASE64)
    .regex(/^[A-Za-z0-9+/]+={0,2}$/),
  mediaType: z.enum(INVOICE_FILE_TYPES),
});
export type InvoiceReadRequest = z.infer<typeof invoiceReadRequestSchema>;

export const INVOICE_VAT_RATES = [0, 8, 18] as const;

export const invoiceReadingLineSchema = z.object({
  name: z.string(),
  code: z.string().nullable(),
  qty: z.number().finite(),
  unit: z.enum(UNITS).nullable(),
  /** Per unit, without VAT, after any discount printed on the line itself. */
  unitPrice: z.number().finite(),
  vatRate: z.union([z.literal(0), z.literal(8), z.literal(18)]),
  /** The line's total as printed, whichever way the invoice prints it; null when there is none. */
  lineTotal: z.number().finite().nullable(),
  /** The reader could not make the line out for certain (handwriting, a fold, a stain). */
  uncertain: z.boolean(),
});
export type InvoiceReadingLine = z.infer<typeof invoiceReadingLineSchema>;

export const invoiceReadingSchema = z.object({
  supplierName: z.string().nullable(),
  supplierFiscal: z.string().nullable(),
  invoiceNumber: z.string().nullable(),
  /** YYYY-MM-DD. */
  date: z.string().nullable(),
  /** False for a coupon or note with no VAT on it at all: its lines are then at 0 %. */
  vatShown: z.boolean(),
  lines: z.array(invoiceReadingLineSchema),
  /** A percentage taken off the whole invoice ("Zbritje 5 %", "Rabat 5 %"). */
  discountPct: z.number().finite().nullable(),
  /** The printed totals, after any discount on the whole invoice. */
  subtotal: z.number().finite().nullable(),
  vatTotal: z.number().finite().nullable(),
  total: z.number().finite().nullable(),
});
export type InvoiceReading = z.infer<typeof invoiceReadingSchema>;

/** `GET /api/invoices/reader`: whether the server has an AI reader configured. */
export const invoiceReaderStatusSchema = z.object({ ai: z.boolean() });
export type InvoiceReaderStatus = z.infer<typeof invoiceReaderStatusSchema>;
